import type { ProjectContext } from "./types/index.js";

/**
 * Formats a project context into a prompt section for AI reasoning.
 * Returns empty string if context is null/undefined or has no meaningful content.
 */
export function buildProjectContextPrompt(
  context: ProjectContext | null | undefined,
): string {
  if (!context) return "";

  const parts: string[] = [];

  if (context.domain) {
    parts.push(`Domain: ${context.domain}`);
  }

  if (context.industry) {
    parts.push(`Industry: ${context.industry}`);
  }

  if (context.governedBy?.length) {
    const refs = context.governedBy
      .map((g) => `${g.name} (${g.authorityLevel.replace(/_/g, " ")})`)
      .join(", ");
    parts.push(`Governed by: ${refs}`);
  }

  if (context.actors?.length) {
    const actors = context.actors
      .map((a) => `${a.name} (${a.role})`)
      .join(", ");
    parts.push(`Key actors: ${actors}`);
  }

  if (context.boundaries?.length) {
    const lines = context.boundaries.map(
      (b) =>
        `- ${b.domainRule} → system ${b.softwareRole.toUpperCase()}${b.explanation ? ` (${b.explanation})` : ""}`,
    );
    parts.push(`\nSoftware boundaries:\n${lines.join("\n")}`);
  }

  if (context.architecture) {
    parts.push(`Architecture: ${context.architecture}`);
  }

  if (parts.length === 0) return "";

  return `PROJECT CONTEXT:\n${parts.join("\n")}`;
}

/**
 * Assemble the full project prompt context: name + description + structured context.
 * All three flow to every AI prompt (runners, generation, evaluation).
 */
export function buildFullProjectContextText(
  project?: {
    name?: string | null;
    description?: string | null;
    context?: unknown;
  } | null,
): string | undefined {
  if (!project) return undefined;
  const parts: string[] = [];
  if (project.name) parts.push(`Project: ${project.name}`);
  if (project.description) parts.push(`Description: ${project.description}`);
  const structured = buildProjectContextPrompt(
    project.context as ProjectContext | null | undefined,
  );
  if (structured) parts.push(structured);
  return parts.length > 0 ? parts.join("\n\n") : undefined;
}

export interface LearnedDismissal {
  summary: string | null;
  reason: string | null;
  kind?: string | null;
}

/**
 * Format active learned dismissals as a prompt section for walkthrough generation.
 * Returns empty string if no dismissals exist.
 */
export function buildLearnedPreferencesPrompt(
  dismissals: LearnedDismissal[],
): string {
  const active = dismissals.filter((d) => d.summary);
  if (active.length === 0) return "";

  const items = active
    .map((d) => {
      const prefix = d.kind === "out_of_scope" ? "[Out of system scope] " : "";
      return `- ${prefix}${d.summary}`;
    })
    .join("\n");

  return [
    `LEARNED PREFERENCES (from project owner feedback):`,
    `The following have been flagged as irrelevant to this project. Do not generate findings about these topics unless you encounter a genuinely new angle not covered by the dismissal reason.`,
    ``,
    items,
  ].join("\n");
}
